import React from 'react';
import { Box, Button, CircularProgress, TextField, Typography } from '@material-ui/core';
import { useCampfireFetch } from '../../../../../../../../global/network/useCampfireFetch';
import { useApiUrl } from '../../../../../../../../global/config/useApiUrl';
import { useCampfireTheme } from '../../../../../../../../theme/useCampfireTheme';

type StripeAccountStatus = {
  connected: boolean;
  accountStatus?: {
    accountId: string;
    accountName: string;
    currency: string;
    hasValidCredentials: boolean;
  };
};

type StripeSettings = {
  productCacheMinutes: number;
};

export function StripeSetups() {
  const { theme } = useCampfireTheme();
  const [isLoading, setIsLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState(false);

  const apiUrl = useApiUrl();
  const manageFetch = useCampfireFetch<StripeAccountStatus>({ defer: true });
  const configFetch = useCampfireFetch<StripeSettings>({ defer: true });
  const saveFetch = useCampfireFetch<any>({ defer: true });

  // State for current form values
  const [publicKey, setPublicKey] = React.useState('');
  const [secretKey, setSecretKey] = React.useState('');

  // State for loaded data
  const [accountStatus, setAccountStatus] = React.useState<StripeAccountStatus | null>(null);

  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load account status (connection info)
      const manageResponse = await manageFetch.run({
        url: `${apiUrl}/wm/integrations/stripe/manage/get`,
        method: 'get',
      });

      const accountData = manageResponse.data.data;
      setAccountStatus(accountData);

      // If connected, load settings
      if (accountData.connected) {
        await configFetch.run({
          url: `${apiUrl}/wm/integrations/stripe/configuration/get`,
          method: 'get',
        });
      }
    } catch (err) {
      setError('Failed to load Stripe configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await saveFetch.run({
        url: `${apiUrl}/wm/integrations/stripe/manage/save`,
        method: 'post',
        data: {
          publicKey: publicKey,
          secretKey: secretKey,
        },
      });

      setSuccess(true);
      // Clear secret key after successful save (security)
      setSecretKey('');
      // Reload data to get updated account info
      await loadData();
    } catch (err) {
      setError((err as any).response?.data?.message || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const isConnected = accountStatus?.connected ?? false;
  const hasValidCredentials = accountStatus?.accountStatus?.hasValidCredentials ?? false;

  // Form is valid if we have public/restricted key and (secret/restricted key OR already connected with valid credentials)
  const isValidPublicKey = publicKey.startsWith('pk_') || publicKey.startsWith('rk_');
  const isValidSecretKey = secretKey.startsWith('sk_') || secretKey.startsWith('rk_');
  const isFormValid = isValidPublicKey && (isValidSecretKey || (isConnected && hasValidCredentials));

  // Form has changes if we have new keys to save
  const hasChanges = publicKey !== '' || secretKey !== '';

  if (isLoading) {
    return (
      <Box display='flex' alignItems='center' justifyContent='center' p={4}>
        <CircularProgress />
        <Typography style={{ marginLeft: 16 }}>Loading Stripe configuration...</Typography>
      </Box>
    );
  }

  return (
    <Box component='form' autoComplete='off'>
      <Typography variant='body2' color='textSecondary' style={{ marginBottom: '16px' }}>
        {isConnected
          ? 'Update your Stripe configuration or API keys.'
          : 'Enter your Stripe API keys to enable payment processing.'}{' '}
        You can find these in your{' '}
        <a href='https://dashboard.stripe.com/apikeys' target='_blank' rel='noopener noreferrer'>
          Stripe Dashboard
        </a>
        .
      </Typography>

      {error && (
        <Box mb={2} p={2} style={{ backgroundColor: '#ffebee', borderRadius: '4px', border: '1px solid #f44336' }}>
          <Typography variant='body2' style={{ color: '#d32f2f' }}>
            {error}
          </Typography>
        </Box>
      )}

      {success && (
        <Box mb={2} p={2} style={{ backgroundColor: '#e8f5e8', borderRadius: '4px', border: '1px solid #4caf50' }}>
          <Typography variant='body2' style={{ color: '#2e7d32' }}>
            Stripe integration saved successfully. Webhook endpoint configured automatically.
          </Typography>
        </Box>
      )}

      {/* Account Status Display */}
      {isConnected && accountStatus?.accountStatus && (
        <Box mb={3} p={2} style={{ backgroundColor: '#e3f2fd', borderRadius: '4px', border: '1px solid #2196f3' }}>
          <Typography variant='subtitle2' style={{ marginBottom: '8px', color: '#1976d2' }}>
            <span role='img' aria-label='checkmark'>
              ✅
            </span>{' '}
            Connected to Stripe
          </Typography>
          <Typography variant='body2'>
            <strong>Account:</strong> {accountStatus.accountStatus.accountName} ({accountStatus.accountStatus.accountId}
            )
          </Typography>
          <Typography variant='body2'>
            <strong>Currency:</strong> {accountStatus.accountStatus.currency.toUpperCase()}
          </Typography>
        </Box>
      )}

      {/* API Keys Section */}
      <Box mb={2} width='100%' maxWidth={600}>
        <TextField
          fullWidth
          variant='outlined'
          label='Stripe Public/Restricted Key'
          placeholder={isConnected ? 'Enter new key (optional)' : 'pk_test_... or rk_...'}
          value={publicKey}
          onChange={(e) => setPublicKey(e.target.value)}
          inputProps={{ autoComplete: 'off' }}
          error={publicKey !== '' && !isValidPublicKey}
          helperText={
            publicKey !== '' && !isValidPublicKey ? 'Key must start with pk_ (public) or rk_ (restricted)' : ''
          }
        />
      </Box>

      <Box mb={3} width='100%' maxWidth={600}>
        <TextField
          fullWidth
          variant='outlined'
          type='password'
          label='Stripe Secret/Restricted Key'
          placeholder={isConnected ? 'Enter new key (optional)' : 'sk_test_... or rk_...'}
          value={secretKey}
          onChange={(e) => setSecretKey(e.target.value)}
          inputProps={{ autoComplete: 'new-password' }}
          error={secretKey !== '' && !isValidSecretKey}
          helperText={
            secretKey !== '' && !isValidSecretKey
              ? 'Key must start with sk_ (secret) or rk_ (restricted)'
              : isConnected
              ? 'Leave empty to keep current secret key'
              : 'Required for initial setup'
          }
        />
      </Box>

      {/* Save Button */}
      <Box mt={2} display='flex' justifyContent='flex-end'>
        <Button
          onClick={handleSave}
          variant='contained'
          disableElevation
          disabled={!hasChanges || !isFormValid || saving}
          style={{
            backgroundColor: theme.color.secondary.main900,
            color: '#ffffff',
            textTransform: 'none',
            padding: '7px 12px',
            fontWeight: 600,
            fontSize: '13px',
            borderRadius: '4px',
            minWidth: '40px',
          }}
        >
          {saving ? 'Saving...' : isConnected ? 'Update Configuration' : 'Save Configuration'}
        </Button>
      </Box>
    </Box>
  );
}
